import { useState, useEffect } from 'react';
import { User } from '@core/generated/gql-types';
import { pluralize } from '@core/util/utils';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';

interface AssignmentMethodProps {
  show: boolean;
  assignedWorkers: User[];
  setAssignedWorkers: (workers: User[]) => void;
  limit: number;
  workers: User[];
  isLoading: boolean;
}

const AssignWorker: React.FC<AssignmentMethodProps> = ({
  show,
  assignedWorkers,
  setAssignedWorkers,
  limit,
  workers,
  isLoading,
}: AssignmentMethodProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredWorkers, setFilteredWorkers] = useState<User[]>([]);
  const { getTerm } = useTemplateStore();

  // Filter workers based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredWorkers(workers);
    } else {
      const filtered = workers.filter((worker) =>
        `${worker.firstName} ${worker.lastName}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      );
      setFilteredWorkers(filtered);
    }
  }, [workers, searchTerm]);

  const handleWorkerToggle = (worker: User) => {
    const isSelected = assignedWorkers.some((w) => w.id === worker.id);

    if (isSelected) {
      // Remove worker
      setAssignedWorkers(assignedWorkers.filter((w) => w.id !== worker.id));
    } else {
      // Add worker (max limit)
      if (assignedWorkers.length < limit) {
        setAssignedWorkers([...assignedWorkers, worker]);
      }
    }
  };

  const isWorkerSelected = (workerId: string) => {
    return assignedWorkers?.some((worker) => worker.id === workerId);
  };

  if (!show) return null;

  return (
    <>
      {/* Worker Selection Modal - shown when postTo is 'assign' */}
      <div className="mb-4 rounded-lg border border-gray-200 bg-white p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold">
            Select {limit} {pluralize('worker', limit)}
          </h3>
          <p className="text-sm text-gray-500">
            Choose workers to assign to this job
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder={`Search ${getTerm(TemplateTerm.WORKER, true)}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 pl-10 text-sm focus:border-blue-500 focus:outline-none"
            />
            <svg
              className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Workers Table */}
        <div className="max-h-75 overflow-y-auto rounded-lg border border-gray-200">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-sm text-gray-500">Loading workers...</div>
            </div>
          ) : filteredWorkers.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-sm text-gray-500">
                {searchTerm
                  ? 'No workers found matching your search.'
                  : 'No workers available.'}
              </div>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">
                    {getTerm(TemplateTerm.WORKER, false)}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredWorkers.map((worker) => (
                  <tr
                    key={worker.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleWorkerToggle(worker)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={isWorkerSelected(worker.id)}
                          onChange={() => handleWorkerToggle(worker)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-300">
                          {worker.profilePictureUrl ? (
                            <img
                              src={worker.profilePictureUrl}
                              alt={`${worker.firstName} ${worker.lastName}`}
                              className="h-8 w-8 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-xs text-gray-600">
                              {worker.firstName?.[0]}
                              {worker.lastName?.[0]}
                            </span>
                          )}
                        </div>
                        <span className="text-sm font-medium">
                          {worker.firstName} {worker.lastName}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {worker.email || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {worker.phoneNumber || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          worker.status === 'ACTIVE'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {worker.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-4 text-xs text-gray-500">
          Selected: {assignedWorkers.length}/{limit} workers
        </div>
      </div>
    </>
  );
};

export default AssignWorker;
